import { Component, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialog } from '@angular/material/dialog';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { BuyContractAllocation, BuyAllocationStatus } from '../buy-contract.type';
import { AuthService } from '../../../auth/auth.service';
import { CardHistoryDialog } from '../card-history-dialog/card-history-dialog';

interface AllocationGroup {
  key: string;
  items: BuyContractAllocation[];
  count: number;
}

@Component({
  selector: 'app-allocation-groups-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatTooltipModule,
  ],
  templateUrl: './allocation-groups-list.html',
  styleUrl: './allocation-groups-list.scss'
})
export class AllocationGroupsList implements OnChanges, OnDestroy {
  @Input() allocations: BuyContractAllocation[] = [];
  @Input() loading = false;
  @Input() loadingText = 'Loading allocations...';
  @Input() emptyMessage = 'No allocations for this contract yet.';
  /** Card-history lookup is admin-only server-side; hide the button on unauthenticated/public views. */
  @Input() showHistory = true;

  private static readonly BASE_COLUMNS = ['player', 'buy_now_price', 'quality', 'status', 'lock_expires_at', 'expire_time'];

  get displayedColumns(): string[] {
    return this.canShowHistory ? [...AllocationGroupsList.BASE_COLUMNS, 'history'] : AllocationGroupsList.BASE_COLUMNS;
  }

  allocationGroups: AllocationGroup[] = [];
  filteredAllocations: BuyContractAllocation[] = [];

  @ViewChild('imagePreviewTpl') imagePreviewTpl!: TemplateRef<any>;
  private overlayRef: OverlayRef | null = null;
  previewImageUrl = '';

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private dialog: MatDialog,
    private authService: AuthService,
  ) { }

  /** The history button calls an admin-only endpoint — hide it for non-admins even if the parent left showHistory at its default. */
  get canShowHistory(): boolean {
    return this.showHistory && this.authService.hasAnyRole('Admin', 'SuperAdmin');
  }

  showCardHistory(cardId: string): void {
    this.dialog.open(CardHistoryDialog, {
      data: { cardId },
      autoFocus: false,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allocations']) {
      this.groupAllocations(this.allocations);
    }
  }

  ngOnDestroy(): void {
    this.hideImagePreview();
  }

  /** Drops Expired rows whose card_id also has a non-expired (or duplicate) entry, so a re-listed card doesn't show its stale expired row. */
  filterRepeatedExpiredCards(items: BuyContractAllocation[]): BuyContractAllocation[] {
    const cardIdCounts = new Map<string, number>();
    items.forEach(item => {
      cardIdCounts.set(item.card_id, (cardIdCounts.get(item.card_id) || 0) + 1);
    });

    return items.filter(item =>
      item.status !== 'Expired' || cardIdCounts.get(item.card_id) === 1
    );
  }

  /** Rows arrive sorted by player_id then expire_time — group consecutive rows instead of re-sorting. */
  groupAllocations(items: BuyContractAllocation[]): void {
    const groupMap = new Map<string, AllocationGroup>();

    if (!items || items.length === 0) {
      this.allocationGroups = [];
      this.filteredAllocations = [];
      return;
    }

    this.filteredAllocations = this.filterRepeatedExpiredCards(items);

    this.filteredAllocations.forEach(item => {
      const key = `${item.player_id}-${item.buy_now_price || 0}-${item.bid_price || 0}-${item.quality}-${item.status}`;

      if (groupMap.has(key)) {
        const group = groupMap.get(key)!;
        group.items.push(item);
        group.count++;
      } else {
        groupMap.set(key, {
          key,
          items: [item],
          count: 1
        });
      }
    });

    this.allocationGroups = Array.from(groupMap.values()).sort((a, b) => {
      if (a.items[0].status !== b.items[0].status) {
        return a.items[0].status === 'Locked' ? -1 : 1;
      }
      const aMin = Math.min(...a.items.map(i => new Date(i.lock_expires_at ?? -1).getTime()));
      const bMin = Math.min(...b.items.map(i => new Date(i.lock_expires_at ?? -1).getTime()));
      return aMin - bMin;
    });
  }

  showImagePreview(origin: HTMLElement, imageUrl: string): void {
    this.previewImageUrl = imageUrl;
    const positionStrategy = this.overlay
      .position().global().centerHorizontally().centerVertically();

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      hasBackdrop: true,
    });

    this.overlayRef.backdropClick().subscribe(() => this.hideImagePreview());
    this.overlayRef.attach(new TemplatePortal(this.imagePreviewTpl, this.viewContainerRef));
  }

  hideImagePreview(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  getAllocationStatusClass(status: BuyAllocationStatus): string {
    switch (status) {
      case 'Locked': return 'status-locked';
      case 'Bought': return 'status-bought';
      case 'Released': return 'status-released';
      case 'Expired': return 'status-expired';
    }
  }

  formatCoins(n: number): string {
    if (!n) return '—';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  }

  formatTimeRemaining(d?: string): string {
    if (!d) return '—';
    const diffMs = new Date(d).getTime() - Date.now();
    if (diffMs <= 0) return 'expired';
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  }

  countByStatus(status: BuyAllocationStatus): number {
    return this.filteredAllocations.filter(a => a.status === status).length;
  }
}
